import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import {
  addCreatorsToSavedListMutationOptions,
  createSavedListMutationOptions,
} from '@/entities/saved-creators-list';
import {
  getSavedListCapacityDialogContent,
  openConstraintAction,
} from '@/entities/workspace/util/capacity-constraint-ux';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { trackEvent } from '@/shared/track';
import { getInfluencerDiscoveryCapacityError } from '@/shared/utils';
import { showDialog } from '@/shared/utils/show-dialog';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';

export function useCreateDiscoverySavedListMutation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const options = createSavedListMutationOptions(queryClient, workspaceId);

  return useMutation({
    ...options,
    onError: async (error) => {
      const capacityError = await getInfluencerDiscoveryCapacityError(error);

      if (capacityError?.message) {
        const feedback = getSavedListCapacityDialogContent(workspace.plan, workspaceId);
        const action = feedback.action;
        showDialog({
          title: feedback.title,
          message: feedback.description,
          confirmLabel: action?.label,
          onConfirm: action
            ? () =>
                openConstraintAction(action, {
                  navigateInternal: (href) => navigate({ to: href }),
                })
            : undefined,
        });
        trackEvent('Saved_list', 'create_list_failed', {
          reason: 'saved_list_capacity_exceeded',
        });
        return;
      }

      trackEvent('Saved_list', 'create_list_failed');
      await showErrorDialog({
        error,
        title: 'Failed to create saved list',
      });
    },
  });
}

export function useAddDiscoveryCreatorsToSavedListMutation() {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const options = addCreatorsToSavedListMutationOptions(queryClient, workspace.id);

  return useMutation({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await options.onSuccess?.(data, variables, onMutateResult, context);
      trackEvent('Saved_list', 'add_creators');
    },
  });
}
